import { Food, FoodSource, getFoodSource } from '@cf-shared/data-model/food';

/**
 * One line of a staple's Source list: a food the staple was built from (food staples plan 4a, #5594).
 * See design-docs/food-staples.md, "Plan 4a".
 */
export interface StapleSourceRow {
  /** the source food's id */
  id: string;
  /** the database it belongs to, shown after its name */
  source: FoodSource;
  /** its name, or undefined when the food could not be read */
  name: string | undefined;
  /** false when the food could not be read, so tapping the line would open nothing */
  canOpen: boolean;
}

/**
 * The lines of a staple's Source list, in the order the staple stores its sources (main source
 * first). The staple document comes from the database and may be damaged or written by an older
 * build, so the id list is repaired here: a value that is not a list reads as empty, and entries that
 * are not official food ids, or repeat an earlier entry, are dropped.
 */
export function buildStapleSourceRows(sourceFoodIds: unknown, foodsById: ReadonlyMap<string, Food>): StapleSourceRow[] {
  if (!Array.isArray(sourceFoodIds)) {
    return [];
  }
  const rows: StapleSourceRow[] = [];
  const seen = new Set<string>();
  for (const id of sourceFoodIds) {
    // if the entry is not an official food id, or was already listed, skip it
    if (typeof id !== 'string' || !id.startsWith('food/$') || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const food = foodsById.get(id);
    const name = typeof food?.name === 'string' && food.name.trim() !== '' ? food.name : undefined;
    rows.push({ id, source: getFoodSource(id) as FoodSource, name, canOpen: name !== undefined });
  }
  return rows;
}
